/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React from "react"
import { useTranslationsMap } from "common/utils/i18n"
import { Alert, Button, Checkbox } from "@duxton-web/ui"
import { CheckboxOptionType } from "@duxton-web/ui/es/checkbox"
import { ReportReason } from "store/duck/global.type"

interface FeedbackReportSubmitProps {
  options?: ReportReason[]
  reasons: string[]
  onChange?(reason: string[]): void
  reportPolicyLink?: string
}

const style = css`
  padding: 39px 34px;

  .dui-checkbox-group-item {
    font-size: 16px;
    line-height: 3;
  }
  .header {
    font-size: 20px;
    color: #1c1c1c;
    margin-bottom: 25px;
  }
  .checkboxes {
    padding: 25px 15px;
    display: flex;
    flex-direction: column;
  }
  .guidelineLink {
    margin: 10px;
  }
`

export const FeedbackReportReason: React.FC<FeedbackReportSubmitProps> = ({
  options = [],
  reasons,
  onChange,
  reportPolicyLink,
}: FeedbackReportSubmitProps) => {
  const textMap = useTranslationsMap({
    report: "feedback.headings.report",
    bannerInfo: "reviewThrottle.bannerInfo",
    bannerGuideline: "reviewThrottle.bannerGuideline",
  })

  const mapOptions: CheckboxOptionType[] = options.map(reason => ({
    label: reason.content,
    value: reason.code,
  }))

  return (
    <div css={style} data-testid="report-reason">
      <div className="header">{textMap.report}</div>
      <Alert
        message={textMap.bannerInfo}
        action={
          <Button
            type="link"
            className="guidelineLink"
            onClick={() => window.open(reportPolicyLink, "_blank")}
          >
            {textMap.bannerGuideline}
          </Button>
        }
        type="info"
        showIcon
      />
      <Checkbox.Group
        value={reasons}
        className="checkboxes"
        options={mapOptions}
        onChange={v => {
          onChange?.(v as string[])
        }}
      />
    </div>
  )
}
